//---------------------------------------------------------
// Imports
//---------------------------------------------------------
import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { get_date } from "common";
import IDBadge from "components/element/IDBadge";
import SimpleButton from "components/element/SimpleButton";
import { LINODE_PAUSED_TOOLTIP, useLinodePaused } from "hooks/useLinodeMutate";
import InstanceInputForm from "components/input/InstanceInputForm";
import {
  describe_instance_error,
  TInstanceFormData,
} from "components/input/instanceFormLogic";
import {
  request_delete_instance,
  request_instance_deprovision,
  request_instance_power,
  request_update_instance,
  SoftPowerAction,
} from "connector/oam/oam";
import { usePopUp } from "hooks/popupHook";
import { useInstances, useRole } from "hooks/query/oamHooks";
import { useTranslation } from "react-i18next";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IVipAttribute } from "types/ha";
import { IInstance, IInstanceInput } from "types/oam";
import { IInstanceHealth } from "hooks/query/healthHook";
import {
  initialPowerSettle,
  isPowerStillSettling,
  POWER_SETTLE_MS,
  powerAcceptedMessage,
  powerConfirmLabel,
  PowerSettleState,
  reducePowerSettle,
  tilePowerActions,
  TilePowerKind,
} from "./powerSettle";
import { FONT_MONO } from "theme";

// Power confirms stay on one line (nowrap). Width is CSS fit-content so the
// dialog grows with the mono label instead of clipping at a fixed px. PopUp
// still caps at maxWidth 90%; overflow scrolls if the sentence exceeds that.
// Other dialogs omit width and keep the 500px default.
const POWER_CONFIRM_DIALOG_WIDTH = "fit-content" as const;

function powerConfirmBody(lead: string, label: string) {
  return (
    <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
      {lead}{" "}
      <Box component="span" sx={{ fontFamily: FONT_MONO }}>
        {label}
      </Box>
      ?
    </Typography>
  );
}

//---------------------------------------------------------
// Functional Component
//---------------------------------------------------------
export default function InstanceCard(props: {
  instance_info: IInstance;
  ha: IVipAttribute;
  health?: IInstanceHealth | null;
  onHealthRefresh?: () => void;
}) {
  const { instance_info, ha, health, onHealthRefresh } = props;
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [elevation, set_elevation] = useState(3);
  const { openPopUp, enableYes } = usePopUp();
  const { instance_list, refetch } = useInstances();
  // Instance CRUD is admin-only (OAM ActInstanceWrite). Hiding the actions
  // for everyone else is UX only — the server enforces it independently.
  const { can_manage_instances } = useRole();

  const instanceRef = useRef<TInstanceFormData | null>(null);
  const instanceListRef = useRef(instance_list);
  instanceListRef.current = instance_list;
  // Power off/on/reboot on this tile only. Gear and delete stay usable.
  const [powerPending, setPowerPending] = useState(false);
  const linodePaused = useLinodePaused();
  const pausedTip = t(LINODE_PAUSED_TOOLTIP);
  const [powerSettle, setPowerSettle] = useState<PowerSettleState | null>(null);
  // Last power button shown for running (off) or offline (on). Transitional
  // statuses keep this until Linode reports running or offline again.
  const lastPowerRef = useRef<TilePowerKind | null>(null);
  const linodeStatus = instance_info.linodeStatus ?? null;
  if (linodeStatus === "running") lastPowerRef.current = "off";
  else if (linodeStatus === "offline") lastPowerRef.current = "on";
  const powerActions = tilePowerActions({
    hasLinodeBinding: instance_info.hasLinodeBinding === true,
    linodeStatus,
    lastPower: lastPowerRef.current,
    powerPending,
    settling: powerSettle !== null,
  });

  useEffect(() => {
    if (!powerSettle) return;
    const remaining = powerSettle.acceptedAt + POWER_SETTLE_MS - Date.now();
    const timer = window.setTimeout(
      () => {
        setPowerSettle((current) =>
          current && current.acceptedAt === powerSettle.acceptedAt
            ? null
            : current,
        );
      },
      Math.max(0, remaining),
    );
    return () => window.clearTimeout(timer);
  }, [powerSettle]);

  useEffect(() => {
    if (!powerSettle) return;
    const next = reducePowerSettle(powerSettle, health ?? null, Date.now());
    if (next !== powerSettle) setPowerSettle(next);
  }, [health, powerSettle]);

  const default_instance_url = `/instance/dashboard?name=${instance_info.name}`;

  // Determine if instance is healthy, active, and clickable
  const isHealthy = health?.isHealthy !== false; // Default to healthy if health is not available yet
  const isActive = instance_info.is_active; // Use the is_active field from the instance
  const isDisabled = !isHealthy || !isActive;

  const handleModify = () => {
    // Extract current instance data to pre-fill the form.
    // `protocol` MUST be included: the form falls back to 'https' for any
    // field it is not given, so omitting it silently flipped an http
    // instance to https on save — and OAM re-derives api_endpoint from
    // protocol, which pointed the proxy at a port that does not speak TLS.
    const currentInstanceData = {
      name: instance_info.name,
      cimage: instance_info.cimage,
      ctag: instance_info.ctag,
      host: instance_info.host,
      port: instance_info.port.toString(),
      protocol: instance_info.protocol,
      version: instance_info.version,
      description: instance_info.description || "",
      is_active: instance_info.is_active,
    };

    const content = (
      <InstanceInputForm
        initialValues={currentInstanceData}
        existing={instanceListRef.current}
        editing_id={instance_info.id}
        onChange={(data) => {
          instanceRef.current = data;
          // Gate Apply on validity — otherwise an invalid edit (bad port,
          // empty host) would be submitted against the live instance
          // (F-INSTANCE-1). enableYes is idempotent, so this is loop-safe.
          enableYes(data.isValid);
        }}
      />
    );

    openPopUp(
      t("Modify Instance"),
      content,
      t("Apply"),
      t("Cancel"),
      async () => {
        if (instanceRef.current) {
          if (!instanceRef.current.isValid) {
            openPopUp(
              t("Error"),
              t("Please correct the highlighted fields."),
              t("OK"),
            );
            return;
          }
          const { isValid, errors, ...payload } = instanceRef.current;
          const res = await request_update_instance(instance_info.id, payload);
          if (res.status === "success") {
            openPopUp(
              t("Success"),
              t("Updated successfully."),
              t("OK"),
              "",
              () => {
                refetch();
                // Trigger health check after successful update
                if (onHealthRefresh) {
                  onHealthRefresh();
                }
              },
            );
          } else
            openPopUp(
              t("Error"),
              t("Failed to update. {{error}}", {
                error: describe_instance_error(res.error),
              }),
              t("OK"),
            );
        }
      },
    );
  };

  const handleDelete = () => {
    const bound = instance_info.hasLinodeBinding === true;
    openPopUp(
      t("Delete instance?"),
      bound
        ? t('Delete "{{name}}"? This destroys the Linode.', {
            name: instance_info.name,
          })
        : t('Delete "{{name}}"? This only removes it from the list.', {
            name: instance_info.name,
          }),
      t("Delete"),
      t("Cancel"),
      async () => {
        const res = bound
          ? await request_instance_deprovision(instance_info.id)
          : await request_delete_instance(instance_info.id);
        if (res.status === "success") {
          await refetch();
          openPopUp(
            t("Success"),
            bound
              ? t("The Linode was destroyed and the instance was removed.")
              : t("Instance deleted successfully."),
            t("OK"),
            "",
            () => {
              navigate("/instance", { replace: true });
            },
          );
        } else {
          const error_message = res.error
            ? bound
              ? res.error
              : describe_instance_error(res.error)
            : t(bound ? "Deprovision failed" : "Failed to delete instance");
          openPopUp(t("Error"), error_message, t("OK"));
        }
      },
    );
  };

  const confirmPower = (
    action: SoftPowerAction,
    title: string,
    body: ReactNode,
  ) => {
    openPopUp(
      title,
      body,
      t(powerConfirmLabel(action)),
      t("Cancel"),
      async () => {
        setPowerPending(true);
        try {
          const res = await request_instance_power(instance_info.id, action);
          if (res.status === "success") {
            const acceptedAt = Date.now();
            setPowerSettle(
              initialPowerSettle(
                action,
                acceptedAt,
                health ? health.isHealthy : null,
              ),
            );
            if (onHealthRefresh) onHealthRefresh();
            // Accepted copy names the action only. The API may still return a Linode id; do not show it.
            openPopUp(t("Success"), t(powerAcceptedMessage(action)), t("OK"));
          } else if (isPowerStillSettling(res)) {
            const acceptedAt = Date.now();
            setPowerSettle(
              initialPowerSettle(
                action,
                acceptedAt,
                health ? health.isHealthy : null,
              ),
            );
            openPopUp(t("Still settling"), t("Still settling"), t("OK"));
          } else {
            openPopUp(
              t("Error"),
              res.error || t("Power request failed"),
              t("OK"),
            );
          }
        } finally {
          setPowerPending(false);
        }
      },
      undefined,
      POWER_CONFIRM_DIALOG_WIDTH,
    );
  };

  const handlePowerOff = () => {
    confirmPower(
      "shutdown",
      t("Power off instance?"),
      powerConfirmBody(t("Shut down"), instance_info.name),
    );
  };

  const handlePowerOn = () => {
    confirmPower(
      "boot",
      t("Power on instance?"),
      powerConfirmBody(t("Power on"), instance_info.name),
    );
  };

  const handleReboot = () => {
    confirmPower(
      "reboot",
      t("Reboot instance?"),
      powerConfirmBody(t("Reboot"), instance_info.name),
    );
  };

  const handleCardClick = () => {
    if (isDisabled) {
      openPopUp(
        t("Instance Unavailable"),
        t(
          "This instance is currently down or unreachable. Please check the instance status and try again.",
        ),
        t("OK"),
      );
    } else {
      navigate(default_instance_url);
    }
  };

  return (
    <Card
      sx={{
        width: "260px",
        height: "500px",
        cursor: isDisabled ? "not-allowed" : "pointer",
        opacity: isDisabled ? 0.6 : 1,
        backgroundColor: isDisabled ? "grey.100" : "background.paper",
      }}
      elevation={isDisabled ? 1 : elevation}
      onMouseOver={() => !isDisabled && set_elevation(5)}
      onMouseOut={() => !isDisabled && set_elevation(3)}
      onClick={handleCardClick}
    >
      <CardContent>
        <Stack gap="8px">
          <Box display="flex" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {t("Created at")}
            </Typography>

            <Typography variant="caption" color="text.disabled">
              {get_date(instance_info.created_at)}
            </Typography>
          </Box>

          <Divider />

          {/* Name owns the whole top line (board #44). Wrap only at a space or
					    after a hyphen: no ellipsis, no mid-word break. */}
          <Box width="100%" data-testid="instance-tile-name-row">
            <Typography
              variant="subtitle1"
              color="textSecondary"
              sx={{
                whiteSpace: "normal",
                overflowWrap: "normal",
                wordBreak: "normal",
                hyphens: "manual",
              }}
            >
              {instance_info.name}
            </Typography>
          </Box>

          {/* ID left; icons right in order Settings, Power, Reboot, Delete.
					    Unbound tiles hide Power/Reboot and keep Settings, Delete. */}
          <Box
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            data-testid="instance-tile-id-row"
          >
            <IDBadge id={instance_info.id} />
            {can_manage_instances && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                data-testid="instance-tile-actions"
              >
                <SimpleButton type="modify" onClick={handleModify} />
                {powerActions.showPower && powerActions.power === "off" && (
                  <SimpleButton
                    type="power_off"
                    onClick={handlePowerOff}
                    disabled={linodePaused || powerActions.powerDisabled}
                    tooltip={
                      linodePaused ? pausedTip : t(powerActions.powerTooltip)
                    }
                  />
                )}
                {powerActions.showPower && powerActions.power === "on" && (
                  <SimpleButton
                    type="power_on"
                    onClick={handlePowerOn}
                    disabled={linodePaused || powerActions.powerDisabled}
                    tooltip={
                      linodePaused ? pausedTip : t(powerActions.powerTooltip)
                    }
                  />
                )}
                {powerActions.showReboot && (
                  <SimpleButton
                    type="reboot"
                    onClick={handleReboot}
                    disabled={linodePaused || powerActions.rebootDisabled}
                    tooltip={
                      linodePaused ? pausedTip : t(powerActions.rebootTooltip)
                    }
                  />
                )}
                <SimpleButton
                  type="delete_strong"
                  onClick={handleDelete}
                  disabled={
                    linodePaused && instance_info.hasLinodeBinding === true
                  }
                  tooltip={
                    linodePaused && instance_info.hasLinodeBinding === true
                      ? pausedTip
                      : undefined
                  }
                />
              </Box>
            )}
          </Box>

          <Divider />

          <Box display="flex" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {t("Host")}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {`${instance_info.host}:${instance_info.port}`}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {t("Version")}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {instance_info.version}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {t("HA State")}
            </Typography>

            <Typography variant="caption" color="text.disabled">
              {ha?.state ?? t("Unknown")}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {t("Health Status")}
            </Typography>

            <Typography
              variant="caption"
              color={isHealthy ? "success.main" : "error.main"}
              sx={{ fontWeight: "bold" }}
            >
              {health === null
                ? t("Checking...")
                : isHealthy
                  ? t("Healthy")
                  : t("Down")}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {t("Activation Status")}
            </Typography>

            <Typography
              variant="caption"
              color={isActive ? "success.main" : "error.main"}
              sx={{ fontWeight: "bold" }}
            >
              {health === null
                ? t("Checking...")
                : isActive
                  ? t("Active")
                  : t("Inactive")}
            </Typography>
          </Box>

          <Divider />

          <Box display="flex" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {t("Tag")}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {instance_info.ctag}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {t("CImage")}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {instance_info.cimage}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {t("API Endpoint")}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="caption" color="text.disabled">
              {instance_info.api_endpoint}
            </Typography>
          </Box>

          <Divider />
          <Box width="100%" overflow="hidden">
            <Typography
              variant="caption"
              color="text.disabled"
              overflow="hidden"
              textOverflow="ellipsis"
              sx={{
                display: "-webkit-box",
                wordBreak: "break-all",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {instance_info.description}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
